import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { ConfirmationService, MessageService } from 'primeng/api';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ToastModule } from 'primeng/toast';
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { ServiceOrderTemplateService } from '../../../services/service-order-template.service';
import { ServiceOrderTemplate } from '../../../models/service-order-template.model';
import { forkJoin } from 'rxjs';

@Component({
  selector: 'app-list-service-order-template',
  standalone: true,
  imports: [
    CommonModule,
    ConfirmDialogModule,
    ToastModule,
    TableModule,
    ButtonModule,
    TooltipModule
  ],
  providers: [ConfirmationService, MessageService],
  templateUrl: './list-service-order-template.component.html'
})
export class ListServiceOrderTemplateComponent implements OnInit {
  templates: ServiceOrderTemplate[] = [];
  loading: boolean = false;
  companyId: number = 1; // TODO: Obter do contexto/auth

  constructor(
    private templateService: ServiceOrderTemplateService,
    private router: Router,
    private confirmationService: ConfirmationService,
    private messageService: MessageService
  ) {}

  ngOnInit(): void {
    this.loadTemplates();
  }

  loadTemplates(): void {
    this.loading = true;
    console.log('Carregando templates para companyId:', this.companyId);
    this.templateService.getAll(this.companyId).subscribe({
      next: (response) => {
        console.log('Resposta completa da API:', response);
        console.log('Response success:', response?.success);
        console.log('Response data:', response?.data);
        
        if (response?.success && response?.data) {
          this.templates = Array.isArray(response.data) ? response.data : [];
          console.log('Templates carregados:', this.templates.length, this.templates);
        } else if (response?.data && Array.isArray(response.data)) {
          // Se não tiver success mas tiver data como array, usar mesmo assim
          this.templates = response.data;
          console.log('Templates carregados (sem success flag):', this.templates.length);
        } else {
          console.warn('Resposta sem dados válidos:', response);
          this.templates = [];
        }
        this.loading = false;
      },
      error: (error) => {
        console.error('Erro completo ao carregar templates:', error);
        console.error('Status:', error?.status);
        console.error('Status Text:', error?.statusText);
        console.error('Error body:', error?.error);
        console.error('URL esperada:', `http://localhost:5297/api/service-order-templates?companyId=${this.companyId}`);
        
        let errorMessage = 'Não foi possível carregar os templates';
        if (error?.status === 404) {
          errorMessage = 'Endpoint de templates não encontrado. Verifique se o backend foi implementado.';
        } else if (error?.status === 0) {
          errorMessage = 'Erro de conexão. Verifique se o backend está rodando.';
        } else if (error?.error?.message) {
          errorMessage = error.error.message;
        }
        
        this.messageService.add({
          severity: 'error',
          summary: 'Erro!',
          detail: errorMessage,
          life: 5000
        });
        this.loading = false;
        this.templates = [];
      }
    });
  }

  createNew(): void {
    this.router.navigate(['/app/service-order-templates/new']);
  }

  edit(id: number): void {
    this.router.navigate(['/app/service-order-templates/edit', id]);
  }

  delete(id: number): void {
    this.confirmationService.confirm({
      message: 'Tem certeza que deseja excluir este template?',
      header: 'Confirmar Exclusão',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Sim',
      rejectLabel: 'Não',
      accept: () => {
        this.templateService.delete(id).subscribe({
          next: (response) => {
            if (response.success) {
              this.messageService.add({
                severity: 'success',
                summary: 'Sucesso!',
                detail: 'Template excluído com sucesso',
                life: 3000
              });
              this.loadTemplates();
            }
          },
          error: (error) => {
            console.error('Erro ao excluir template:', error);
            this.messageService.add({
              severity: 'error',
              summary: 'Erro!',
              detail: 'Não foi possível excluir o template',
              life: 3000
            });
          }
        });
      }
    });
  }

  toggleActive(template: ServiceOrderTemplate): void {
    const newActiveState = !template.active;
    
    // Se está ativando um template, desativar todos os outros primeiro
    if (newActiveState) {
      // Encontrar todos os templates ativos (exceto o atual)
      const otherActiveTemplates = this.templates.filter(t => t.active && t.id !== template.id);
      
      if (otherActiveTemplates.length > 0) {
        // Desativar todos os outros templates ativos primeiro
        const deactivationObservables = otherActiveTemplates.map(t => 
          this.templateService.toggleActive(t.id!, false)
        );
        
        forkJoin(deactivationObservables).subscribe({
          next: () => {
            // Depois de desativar os outros, ativar o selecionado
            this.activateTemplate(template, newActiveState);
          },
          error: (error) => {
            console.error('Erro ao desativar templates anteriores:', error);
            this.messageService.add({
              severity: 'error',
              summary: 'Erro!',
              detail: 'Não foi possível alterar o status do template',
              life: 3000
            });
            // Reverter mudança visual
            template.active = !newActiveState;
          }
        });
      } else {
        // Não há outros templates ativos, apenas ativar o novo
        this.activateTemplate(template, newActiveState);
      }
    } else {
      // Está desativando, pode fazer diretamente
      this.activateTemplate(template, newActiveState);
    }
  }

  private activateTemplate(template: ServiceOrderTemplate, newActiveState: boolean): void {
    this.templateService.toggleActive(template.id!, newActiveState).subscribe({
      next: (response) => {
        if (response.success) {
          template.active = newActiveState;
          
          // Se ativou, garantir que os outros estão desativados localmente
          if (newActiveState) {
            this.templates.forEach(t => {
              if (t.id !== template.id && t.active) {
                t.active = false;
              }
            });
          }
          
          this.messageService.add({
            severity: 'success',
            summary: 'Sucesso!',
            detail: `Template ${newActiveState ? 'ativado' : 'desativado'} com sucesso`,
            life: 3000
          });
        }
      },
      error: (error) => {
        console.error('Erro ao alterar status do template:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Erro!',
          detail: 'Não foi possível alterar o status do template',
          life: 3000
        });
        // Reverter mudança visual
        template.active = !newActiveState;
      }
    });
  }

  getChecklistItemsCount(template: ServiceOrderTemplate): number {
    return template.checklistItems?.length || 0;
  }

  getCustomFieldsCount(template: ServiceOrderTemplate): number {
    // Backend pode retornar customFieldDefinitions (completos) ou customFieldDefinitionIds (apenas IDs)
    // ou customFields (campos criados diretamente no template)
    if (template.customFieldDefinitions && template.customFieldDefinitions.length > 0) {
      return template.customFieldDefinitions.length;
    } else if (template.customFieldDefinitionIds && template.customFieldDefinitionIds.length > 0) {
      return template.customFieldDefinitionIds.length;
    } else if (template.customFields && template.customFields.length > 0) {
      return template.customFields.length;
    }
    return 0;
  }
}

